/**
 * StaffReports page — form-fill reports for all 3 types.
 *
 * Distinct from the "Operations Dashboard" (Phase 10A computed dashboard).
 * This is the canonical "Reports" area for staff to create/submit/download.
 *
 * data-testid: candeza-reports-staff-*
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { PageContainer } from '@/components/layout';
import { useAuth } from '@/context';
import type { Role } from '@/types';
import { ReportForm } from './staff/ReportForm';
import { MyDrafts } from './staff/MyDrafts';
import { MyReports } from './staff/MyReports';
import { UserReportStats } from './staff/UserReportStats';
import { AdminReportDashboard } from './staff/AdminReportDashboard';
import type { ReportType, StaffReport } from './staff/api';

// ---------------------------------------------------------------------------
// Which report types can each role access?
// ---------------------------------------------------------------------------

const ROLE_TYPE_MAP: Record<string, ReportType[]> = {
  staff:   ['instructor'],
  manager: ['office-manager', 'business-dev-executive'],
  admin:   ['instructor', 'office-manager', 'business-dev-executive'],
  owner:   ['instructor', 'office-manager', 'business-dev-executive'],
};

const TYPE_LABELS: Record<ReportType, string> = {
  'instructor': 'Student Progress Report',
  'office-manager': 'Office Manager Report',
  'business-dev-executive': 'Business Dev Executive Report',
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  gap: 0;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: none;
  border-bottom: 3px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  background: none;
  cursor: pointer;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ $active, theme }) => ($active ? theme.typography.fontWeights.semibold : theme.typography.fontWeights.normal)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  transition: all 0.15s;
  margin-bottom: -2px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const SectionCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
`;

const SectionHeading = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  padding-left: ${({ theme }) => theme.spacing.sm};
`;

type ViewTab = 'new' | 'drafts' | 'my-reports' | 'stats' | 'admin-dashboard';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const StaffReports: React.FC = () => {
  const { user } = useAuth();
  const role = (user?.role ?? 'staff') as Role;

  // 11E.8 — session-page "Grades / Add grades" deep-link
  // (?student=<id>&studentName=<name>&period_start=&period_end=). Present
  // only when a teacher followed the link from SessionDetailPage's rail.
  const [searchParams] = useSearchParams();
  const prefillStudentId = searchParams.get('student') ?? undefined;
  const prefillStudentName = searchParams.get('studentName') ?? undefined;
  const prefillPeriodStart = searchParams.get('period_start') ?? undefined;
  const prefillPeriodEnd = searchParams.get('period_end') ?? undefined;
  const hasGradesPrefill = Boolean(prefillStudentId || prefillStudentName);

  const allowedTypes = ROLE_TYPE_MAP[role] ?? ROLE_TYPE_MAP.staff;
  const [activeType, setActiveType] = useState<ReportType>(
    hasGradesPrefill && allowedTypes.includes('instructor') ? 'instructor' : allowedTypes[0],
  );
  const [activeView, setActiveView] = useState<ViewTab>(hasGradesPrefill ? 'new' : 'drafts');
  const [resumeDraft, setResumeDraft] = useState<StaffReport | null>(null);

  const isAdmin = role === 'admin' || role === 'owner';

  const handleResume = (draft: StaffReport) => {
    setResumeDraft(draft);
    setActiveView('new');
  };

  const handleRefillOpened = (newDraft: StaffReport) => {
    setResumeDraft(newDraft);
    setActiveView('new');
  };

  const handleSaved = () => {
    setActiveView('drafts');
  };

  const handleSubmitted = () => {
    setResumeDraft(null);
    setActiveView('my-reports');
  };

  return (
    <PageContainer
      title="Reports"
      subtitle="Submit and manage staff reports"
      data-testid="candeza-reports-staff-page"
      data-flow="reports-page"
    >
      {/* Report type selector (if multiple types) */}
      {allowedTypes.length > 1 && (
        <TabBar data-testid="candeza-reports-staff-type-tabs">
          {allowedTypes.map((t) => (
            <Tab
              key={t}
              $active={activeType === t}
              onClick={() => { setActiveType(t); setActiveView('drafts'); setResumeDraft(null); }}
              data-testid={`candeza-reports-staff-type-tab-${t}`}
              data-flow="reports-type-tab"
            >
              {TYPE_LABELS[t]}
            </Tab>
          ))}
        </TabBar>
      )}

      {/* View tabs */}
      <TabBar data-testid="candeza-reports-staff-view-tabs">
        <Tab $active={activeView === 'new'} onClick={() => setActiveView('new')} data-testid="candeza-reports-staff-tab-new" data-flow="reports-new-tab">
          ✏️ New Report
        </Tab>
        <Tab $active={activeView === 'drafts'} onClick={() => setActiveView('drafts')} data-testid="candeza-reports-staff-tab-drafts">
          📝 My Drafts
        </Tab>
        <Tab $active={activeView === 'my-reports'} onClick={() => setActiveView('my-reports')} data-testid="candeza-reports-staff-tab-my-reports">
          📋 My Reports
        </Tab>
        <Tab $active={activeView === 'stats'} onClick={() => setActiveView('stats')} data-testid="candeza-reports-staff-tab-stats">
          📊 My Stats
        </Tab>
        {isAdmin && (
          <Tab $active={activeView === 'admin-dashboard'} onClick={() => setActiveView('admin-dashboard')} data-testid="candeza-reports-staff-tab-admin-dashboard">
            🏆 Admin Dashboard
          </Tab>
        )}
      </TabBar>

      {/* Content areas */}
      {activeView === 'new' && (
        <SectionCard data-testid="candeza-reports-staff-new-form-section">
          <SectionHeading>
            {TYPE_LABELS[activeType]}
            {resumeDraft && (
              <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 400, color: '#64748b' }}>
                (resuming draft {resumeDraft.id.slice(0, 8)}…)
              </span>
            )}
          </SectionHeading>
          <ReportForm
            reportType={activeType}
            existingDraft={resumeDraft}
            onSaved={handleSaved}
            onSubmitted={handleSubmitted}
            prefillStudentId={!resumeDraft ? prefillStudentId : undefined}
            prefillStudentName={!resumeDraft ? prefillStudentName : undefined}
            prefillPeriodStart={!resumeDraft ? prefillPeriodStart : undefined}
            prefillPeriodEnd={!resumeDraft ? prefillPeriodEnd : undefined}
          />
        </SectionCard>
      )}

      {activeView === 'drafts' && (
        <SectionCard data-testid="candeza-reports-staff-drafts-section">
          <SectionHeading>My Drafts</SectionHeading>
          <MyDrafts reportType={activeType} onResume={handleResume} />
        </SectionCard>
      )}

      {activeView === 'my-reports' && (
        <SectionCard data-testid="candeza-reports-staff-my-reports-section">
          <SectionHeading>My Submitted Reports</SectionHeading>
          <MyReports reportType={activeType} onRefillOpened={handleRefillOpened} />
        </SectionCard>
      )}

      {activeView === 'stats' && (
        <SectionCard data-testid="candeza-reports-staff-stats-section">
          <SectionHeading>My Stats</SectionHeading>
          <UserReportStats />
        </SectionCard>
      )}

      {activeView === 'admin-dashboard' && isAdmin && (
        <SectionCard data-testid="candeza-reports-staff-admin-section">
          <SectionHeading>Admin Report Dashboard</SectionHeading>
          <AdminReportDashboard />
        </SectionCard>
      )}
    </PageContainer>
  );
};

